import {
  addDays,
  addMonths,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
  subDays,
  subMonths,
} from 'date-fns';

interface StateValidationRule {
  termLimitMonths: number;
  errorMessage: string;
  entityExclusion: boolean;
}

export const VALIDATION_BY_STATE: Record<string, StateValidationRule> = {
  CA: {
    termLimitMonths: 3,
    errorMessage: 'Buyer agreements must be less than 3 months',
    entityExclusion: true,
  },
  FL: {
    termLimitMonths: 12,
    errorMessage: 'Buyer agreements must be less than 12 months',
    entityExclusion: false,
  },
};

export function isEndDateBeyondTermLimit(
  endDate: string | null | undefined,
  startDate: string | null | undefined,
  termLimitMonths: number,
): boolean {
  if (!endDate || !startDate) return false;

  const start = startOfDay(parseISO(startDate));
  const end = startOfDay(parseISO(endDate));
  const limit = subDays(addMonths(start, termLimitMonths), 1);

  return isAfter(end, limit);
}

export function isStartDateBeforeTermLimit(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  termLimitMonths: number,
): boolean {
  if (!startDate || !endDate) return false;

  const start = startOfDay(parseISO(startDate));
  const end = startOfDay(parseISO(endDate));
  const limit = addDays(subMonths(end, termLimitMonths), 1);

  return isBefore(start, limit);
}

export const END_DATE_BEFORE_START_DATE_ERROR =
  'End date must be on or after the start date';

export function isEndDateBeforeStartDate(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): boolean {
  if (!startDate || !endDate) return false;

  const start = startOfDay(parseISO(startDate));
  const end = startOfDay(parseISO(endDate));

  return isBefore(end, start);
}

interface TermLengthErrors {
  startDateError: string | null;
  endDateError: string | null;
}

const NO_ERRORS: TermLengthErrors = {
  startDateError: null,
  endDateError: null,
};

export function validateAgreementTermLength(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  agentState: string | null | undefined,
  clientEntityTypes: (string | null | undefined)[],
): TermLengthErrors {
  if (!agentState) return NO_ERRORS;

  const rule = VALIDATION_BY_STATE[agentState];
  if (!rule) return NO_ERRORS;

  if (rule.entityExclusion) {
    const hasEntityBuyer = clientEntityTypes.some((t) => t === 'entity');
    if (hasEntityBuyer) return NO_ERRORS;
  }

  const isInvalid = isEndDateBeyondTermLimit(
    endDate,
    startDate,
    rule.termLimitMonths,
  );
  if (!isInvalid) return NO_ERRORS;

  return {
    startDateError: rule.errorMessage,
    endDateError: ' ',
  };
}
